import { debounce } from "lodash/fp";
import React, { useContext, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  TextField,
  Checkbox,
  Typography,
  IconButton
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQuery, useMutation } from "@apollo/client/react";
import OverwatchModal from "@/components/OverwatchModal";
import CurrentUserContext from "@/Contexts/CurrentUser";
import { GetManagersDocument, GetCompanyTagsDocument, CreateProjectTemplateDocument } from "@/graphql/generated/graphql";
import { titleizeCamelCase } from "@/utils/helperFunctions";
import { displayNotification } from "@/utils/notifications";

const normalDebounce = debounce(500);

const DEFAULT_TEMPLATE_STATE = {
  title: "",
  description: "",
  managerIds: [] as string[],
  tagIds: [] as string[],
  activities: [{
    title: "",
    description: "",
    colorCode: "#000000"
  }]
};

interface CreateProjectTemplateModalProps {
  handleClose: () => void;
}

const CreateProjectTemplateModal: React.FC<CreateProjectTemplateModalProps> = ({
  handleClose,
}) => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const { currentUser } = useContext(CurrentUserContext);
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;
  const [template, setTemplate] = useState({
    ...DEFAULT_TEMPLATE_STATE,
    activities: [{
      title: "",
      description: "",
      colorCode: "#000000"
    }]
  });

  const { data: managers } = useQuery(GetManagersDocument, {
    skip: !currentUser?.defaultCompany?.id,
  });

  const { data: tagsData } = useQuery(GetCompanyTagsDocument);

  const [createProjectTemplate] = useMutation(CreateProjectTemplateDocument);

  const handleOnChange = (key: string, value: string) => {
    debounceFunc.cancel();
    debounceFunc(() => {
      setTemplate((prev) => ({
        ...prev,
        [key]: value,
      }));
    });
  }

  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    createProjectTemplate({
      variables: {
        projectTemplate: template
      },
      onCompleted: () => {
        displayNotification({
          type: "success",
          text: "Project Template created successfully",
        });
        handleClose();
      },
      onError: () => {
        displayNotification({
          type: "error",
          text: "Unable to create Project Template, try again later.",
        });
      },
      refetchQueries: ["getProjectTemplates"],
    });
  };

  const handleActivityChange = (index: number, field: string, value: string) => {
    setTemplate(prev => {
      const newActivities = [...prev.activities];
      newActivities[index] = {
        ...newActivities[index],
        [field]: value
      };
      return {
        ...prev,
        activities: newActivities
      };
    });
  };

  const addActivity = () => {
    setTemplate(prev => ({
      ...prev,
      activities: [...prev.activities, {
        title: "",
        description: "",
        colorCode: "#000000"
      }]
    }));
  };

  const removeActivity = (index: number) => {
    if (template.activities.length > 1) {
      setTemplate(prev => ({
        ...prev,
        activities: prev.activities.filter((_, i) => i !== index)
      }));
    }
  };

  return (
    <OverwatchModal
      size="sm"
      title="New Template Form"
      caption="Fill out the details below to create new project template"
      handleClose={handleClose}
      primaryButtonAction={handleSubmit}
      primaryButtonText="Create"
      secondaryButtonAction={handleClose}
      secondaryButtonText="Cancel"
    >
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              autoComplete="template-title"
              required
              defaultValue={template.title}
              fullWidth
              id="template-title"
              label="Title"
              name="template-title"
              onChange={(e) => { handleOnChange("title", e.target.value) }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              autoComplete="template-description"
              multiline
              required
              rows={4}
              defaultValue={template.description}
              fullWidth
              id="template-description"
              label="Description"
              name="template-description"
              onChange={(e) => { handleOnChange("description", e.target.value) }}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              multiple
              id="managers-selector"
              options={managers?.getManagers || []}
              getOptionLabel={(option) => `${option.name}${option.resourceMeta?.email ? ` (${option.resourceMeta?.email})` : ''}`}
              disableCloseOnSelect
              onChange={(_, newValue) => {
                setTemplate(prev => ({
                  ...prev,
                  managerIds: newValue.map(manager => manager.id)
                }))
              }}
              renderOption={(props, option, { selected }) => {
                const { ...otherProps } = props;
                return (
                  <li {...otherProps} key={option.id}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {titleizeCamelCase(option.name)}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Managers"
                  placeholder="Select managers"
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              multiple
              id="tags-selector"
              options={tagsData?.getCompanyTags || []}
              getOptionLabel={(option) => option.title}
              disableCloseOnSelect
              onChange={(_, newValue) => {
                setTemplate(prev => ({
                  ...prev,
                  tagIds: newValue.map(tag => tag.id)
                }))
              }}
              renderOption={(props, option, { selected }) => {
                const { ...otherProps } = props;
                return (
                  <li {...otherProps} key={option.id}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.title}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Select tags"
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">Activities</Typography>
            </Box>
            {template.activities.map((activity, index) => (
              <Box
                key={index}
                sx={{
                  mb: 3,
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}
              >
                <Grid container spacing={2}>
                  <Grid
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                    size={12}>
                    <Grid container spacing={2} sx={{ flex: 1, mr: 1 }}>
                      <Grid size={12}>
                        <TextField
                          required
                          fullWidth
                          label="Activity Title"
                          value={activity.title}
                          onChange={(e) => handleActivityChange(index, 'title', e.target.value)}
                        />
                      </Grid>
                      <Grid size={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label="Activity Description"
                          value={activity.description}
                          onChange={(e) => handleActivityChange(index, 'description', e.target.value)}
                        />
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          type="color"
                          fullWidth
                          label="Color"
                          value={activity.colorCode}
                          onChange={(e) => handleActivityChange(index, 'colorCode', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                    {template.activities.length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeActivity(index)}
                        sx={{ mt: 1 }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button
              variant="outlined"
              onClick={addActivity}
              sx={{ mt: 1 }}
            >
              Add Activity
            </Button>
          </Grid>
        </Grid>
      </Box>
    </OverwatchModal>
  );
};

export default CreateProjectTemplateModal;
